/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable, OnDestroy } from '@angular/core';
import { DiConfigService } from '@app/di/di-config.service';
import { ContextService } from '@app/di/context.service';
import { UtilService } from '@app/util/util.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { REPORT_STATUS } from '@app/di/integrations/reporting/reporting-contants';
import { xmlTemplates } from '@app/di/xml-templates';
import { appConstants } from '@app/common/app-constants';
import { isValidValue } from '@core/lib/helpers/helpers';
import { Subscription } from 'rxjs';

const log = new Logger('XmlTemplateManagerService');

@Injectable({
  providedIn: 'root'
})
export class XmlTemplateManagerService implements OnDestroy {
  private UVIEWER_CONFIG;
  private VIEWERCONTEXT_CONFIG;
  private REPORTING_CONFIG;
  private APPLAUNCHER_CONFIG;
  private WFM_CONTEXT_CONFIG;
  private xmlList = [
    { xmlName: 'ccowsetcontext', type: 'CCOW' },
    { xmlName: 'close', type: 'DI' },
    { xmlName: 'generalnode', type: 'DI' },
    { xmlName: 'isitesetcontext', type: 'iSite' },
    { xmlName: 'load', type: 'DI' },
    { xmlName: 'reportsetcontext', type: 'PS360' },
    { xmlName: 'stringnode', type: 'DI' },
    { xmlName: 'subscribe', type: 'DI' },
    { xmlName: 'uviewersetcontext', type: 'UV' },
    { xmlName: 'comparisoncontext', type: 'ViewerContext' },
    { xmlName: 'genericpluginsetcontext', type: 'GenericReporting' },
    { xmlName: 'terminateapplicationcontext', type: 'TerminateApplication' },
    { xmlName: 'applaunchersetcontext', type: 'AppLauncher' },
    { xmlName: 'patientinfosetcontext', type: 'UV' },
    { xmlName: 'genericpluginloginoperation', type: 'LoginOperation' }
  ];
  private reportOwnerInstance;
  private reportModuleName;
  private isLightThemeEnabled: boolean;
  private userPreferencesObserver: Subscription;

  xmlTemplates: any;

  constructor(
    private diConfigService: DiConfigService,
    private contextService: ContextService,
    private utilService: UtilService,
    private usrPreferenceService: UserPreferenceService
  ) {}

  initialize() {
    log.info('Desktop Integration - xmlTemplateManagerService initialize() START');
    this.UVIEWER_CONFIG = this.diConfigService.UVIEWER_CONFIG;
    this.VIEWERCONTEXT_CONFIG = this.diConfigService.VIEWER_CONTEXT_CONFIG;
    this.REPORTING_CONFIG = this.diConfigService.REPORTING_CONFIG;
    this.APPLAUNCHER_CONFIG = this.diConfigService.APP_LAUNCHER_CONFIG;
    this.WFM_CONTEXT_CONFIG = this.diConfigService.WFM_CONTEXT_CONFIG;

    this.reportOwnerInstance = this.REPORTING_CONFIG.OWNERINSTANCE;
    this.reportModuleName = this.REPORTING_CONFIG.MODULENAME;
    this.isLightThemeEnabled = !!this.usrPreferenceService.getUserLevelPreferenceFromCache('LightsMode');
    this.userPreferencesObserver = this.usrPreferenceService.themeObserver$.asObservable().subscribe(value => {
      this.isLightThemeEnabled = value;
    });
    return new Promise((resolve, reject) => {
      this.diConfigService.getXmlTemplates().then(
        data => {
          log.info('Desktop Integration - xmlTemplateManagerService initialize() promise resolved');
          this.xmlTemplates = data;
          resolve({
            errorCodeList: this.validateXMLTemplates(this.xmlTemplates)
          });
        },
        reason => {
          log.error(
            'Desktop Integration - xmlTemplateManagerService initialize():: ' +
              'diConfigService.getXmlTemplates() promise rejected. Error : ' +
              reason.statusText
          );
          reject(reason);
        }
      );
      log.info('Desktop Integration - xmlTemplateManagerService initialize() END');
    });
  }

  ngOnDestroy(): void {
    this.userPreferencesObserver && this.userPreferencesObserver.unsubscribe();
  }

  validateXMLTemplates(xmlTemplates) {
    const errorCodeList = [];
    for (const xmlType of this.xmlList) {
      if (!{}.hasOwnProperty.call(xmlTemplates, xmlType.xmlName)) {
        log.error(
          "Desktop Integration - xmlTemplateManagerService - validateXMLTemplates() : '" +
            xmlType.xmlName +
            "' template is a invalid, Please check the schema file"
        );
        errorCodeList.push(xmlType);
      } else {
        if (_.isNull(xmlTemplates[xmlType.xmlName]) || _.isUndefined(xmlTemplates[xmlType.xmlName])) {
          log.error(
            'Desktop Integration - xmlTemplateManagerService - validateXMLTemplates() : ' +
              'Required template is empty : ' +
              xmlType.xmlName
          );
          errorCodeList.push(xmlType);
        }
      }
    }
    log.info('Desktop Integration - xmlTemplateManagerService - errorCodeList : ' + errorCodeList);
    return errorCodeList;
  }

  getReportingSetContextXml(params) {
    log.info('Desktop Integration - xmlTemplateManagerService getReportingSetContextXml()');
    let reportingSetContextXML = this.xmlTemplates.genericpluginsetcontext;
    reportingSetContextXML = reportingSetContextXML.replace('{{SOURCENAME}}', this.reportOwnerInstance);
    reportingSetContextXML = reportingSetContextXML.replace('{{CTXCONTROLLER}}', this.reportOwnerInstance);
    reportingSetContextXML = reportingSetContextXML.replace('{{APPNAME}}', this.reportModuleName);
    reportingSetContextXML = reportingSetContextXML.replace('{{MODULENAME}}', this.reportModuleName);
    reportingSetContextXML = reportingSetContextXML.replace('{{USERNAME}}', this.REPORTING_CONFIG.USERNAME);
    // Reporting application path need to pass for START action only
    const reportingAppPath = params.Action === 'START' ? this.REPORTING_CONFIG.APPPATH : '';
    reportingSetContextXML = reportingSetContextXML.replace('{{REPORTING_APPPATH}}', reportingAppPath);
    if (params.Action === 'START') {
      const configKeyNode = this.createDataNode('REPORTING_SESSION_ID', 1, this.diConfigService.createConfigKey());
      reportingSetContextXML = reportingSetContextXML.replace('<p1:configkey/>', configKeyNode);
    }
    reportingSetContextXML = reportingSetContextXML.replace('{{OPERATION}}', JSON.stringify(params));
    const reportingwrapper = 'ReportingWrapper_' + this.diConfigService.DI_CONFIG.WFMPLUGINS_VERSION;
    reportingSetContextXML = reportingSetContextXML.replace('{{REPORTING_WRAPPER}}', reportingwrapper);
    return reportingSetContextXML;
  }

  convertHTMLEntities(stringToConvert) {
    return stringToConvert.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  setViewerCurrentItemContext(examIdent) {
    let patientInfoSetContextXML = this.xmlTemplates.patientinfosetcontext;
    if (this.contextService.getCurrentOnlineContextItem(examIdent)) {
      patientInfoSetContextXML = patientInfoSetContextXML.replace('{{SOURCENAME}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
      const patientNameNode = this.createDataNode(
        'PATIENT_NAME',
        1,
        this.contextService.getCurrentOnlineContextItem(examIdent).patientName
      );
      patientInfoSetContextXML = patientInfoSetContextXML.replace('<p1:patientName/>', patientNameNode);
      const patientNameFormatNode = this.createDataNode(
        'PATIENT_NAME_FORMAT',
        1,
        this.contextService.getCurrentOnlineContextItem(examIdent).patientNameFormat
      );
      patientInfoSetContextXML = patientInfoSetContextXML.replace('<p1:patientNameFormat/>', patientNameFormatNode);
      const patientGenderNode = this.createDataNode(
        'PATIENT_GENDER',
        1,
        this.contextService.getCurrentOnlineContextItem(examIdent).patientGender
      );
      patientInfoSetContextXML = patientInfoSetContextXML.replace('<p1:patientGender/>', patientGenderNode);
      const patientDOBNode = this.createDataNode(
        'DOB',
        1,
        this.contextService.getCurrentOnlineContextItem(examIdent).DOB
      );
      patientInfoSetContextXML = patientInfoSetContextXML.replace('<p1:DOB/>', patientDOBNode);
      const modalityNode = this.createDataNode(
        'MODALITY',
        1,
        this.contextService.getCurrentOnlineContextItem(examIdent).modality
      );
      patientInfoSetContextXML = patientInfoSetContextXML.replace('<p1:modality/>', modalityNode);
      const patientIDNode = this.createDataNode(
        'PATIENT_ID_WITH_ISSUER',
        1,
        this.contextService.getCurrentOnlineContextItem(examIdent).patientIdWithIssuer + 'Remove'
      );
      patientInfoSetContextXML = patientInfoSetContextXML.replace('<p1:patientIdWithIssuer/>', patientIDNode);
      //Add logic for finding diff patient
      const NCD = UtilService.parseInt(examIdent.split(':')[1]);
      //To identify whether comparisons are present
      this.utilService.getComparisons(NCD);
      const diffPatFlag = this.utilService.getDifferentPatientFlag() ? 'Y' : 'N';
      const diffPatientCompNode = this.createDataNode('DIFFERENT_PATIENT_IN_COMPARISON', 1, diffPatFlag);
      patientInfoSetContextXML = patientInfoSetContextXML.replace(
        '<p1:differentPatientInComparison/>',
        diffPatientCompNode
      );
    }
    patientInfoSetContextXML = this.addGlobalExamIdent(patientInfoSetContextXML, examIdent);
    patientInfoSetContextXML = this.convertHTMLEntities(patientInfoSetContextXML);
    return patientInfoSetContextXML;
  }

  addGlobalExamIdent(patientInfoSetContextXML, examIdent) {
    const context = this.contextService.getCurrentOnlineContextItem(examIdent);
    let globalExamParams = '';

    if (UtilService.isGlobalExam(context)) {
      const globalExamNode = this.createDataNode('IS_GLOBAL_EXAM', 1, 'true');
      const siteIdNode = this.createDataNode('SITE_ID', 1, context.siteId);
      globalExamParams = globalExamNode + siteIdNode;
    }
    patientInfoSetContextXML = patientInfoSetContextXML.replace('<p1:globalexams/>', globalExamParams);
    return patientInfoSetContextXML;
  }

  getViewerSetContextXml(jwtToken: string, actionType?: string) {
    log.info('Desktop Integration - xmlTemplateManagerService getViewerSetContextXml() jwtToken: ' + jwtToken);
    let viewerSetContextXML = this.xmlTemplates.uviewersetcontext;
    viewerSetContextXML = viewerSetContextXML.replace('{{SOURCENAME}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    viewerSetContextXML = viewerSetContextXML.replace('{{OWNERINSTANCE}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    viewerSetContextXML = viewerSetContextXML.replace('{{MESSAGE_ID}}', this.getUniqueID());
    viewerSetContextXML = viewerSetContextXML.replace('{{MODULENAME}}', this.UVIEWER_CONFIG.MODULENAME);
    viewerSetContextXML = viewerSetContextXML.replace('{{STUDY_CLICK_TIME}}', this.getStudyClickTime());
    viewerSetContextXML = viewerSetContextXML.replace('{{CTXCONTROLLER}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    viewerSetContextXML = viewerSetContextXML.replace('{{SERVER}}', this.UVIEWER_CONFIG.SERVER);
    viewerSetContextXML = viewerSetContextXML.replace('{{LANGUAGE}}', this.UVIEWER_CONFIG.LANGUAGE);
    viewerSetContextXML = viewerSetContextXML.replace('{{JWT}}', jwtToken);
    viewerSetContextXML = viewerSetContextXML.replace('{{NOWAITDLG}}', this.UVIEWER_CONFIG.NOWAITDLG);
    viewerSetContextXML = viewerSetContextXML.replace('{{REQUEST_PRIORS}}', this.UVIEWER_CONFIG.REQUEST_PRIORS);
    viewerSetContextXML = viewerSetContextXML.replace('{{UNFOCUSED}}', this.UVIEWER_CONFIG.UNFOCUSED);
    viewerSetContextXML = viewerSetContextXML.replace('{{LOGIN}}', this.UVIEWER_CONFIG.LOGIN);
    viewerSetContextXML = viewerSetContextXML.replace(
      '{{CURRENT_CONTEXT_INDEX}}',
      this.contextService.getCurrentIndexForViewer().toString()
    );
    viewerSetContextXML = viewerSetContextXML.replace('{{SECURITY}}', this.UVIEWER_CONFIG.SECURITY);
    let closeAsDoneNode = '';
    if (this.contextService.getCloseFlag()) {
      const doneOnTabClose =
        actionType != 'externalDiscard' &&
        actionType != 'skipExam' &&
        (actionType == 'externalKeep' ||
          !!this.usrPreferenceService.getUserPreferenceFromCache('apply-viewer-done-on-tab-closure'));
      if (doneOnTabClose) {
        closeAsDoneNode = this.createDataNode('CLOSE_ASDONE', 1, 'TRUE');
      } else {
        closeAsDoneNode = this.createDataNode('CLOSE_ASDONE', 1, 'FALSE');
      }
    }
    viewerSetContextXML = viewerSetContextXML.replace('<p1:closeasdone/>', closeAsDoneNode);
    const examIdentNode = this.createDataNode(
      'EXAM_IDENT',
      this.contextService.getContextItemCount(),
      this.contextService.getStudyIdentifiersForViewer()
    );
    viewerSetContextXML = viewerSetContextXML.replace('<p1:examident/>', examIdentNode);
    if (this.contextService.getContextItemCount() !== 0) {
      const patientNode = this.createPatientNode(
        this.contextService.getStudyIdentifiersForViewer(),
        this.contextService.getContextItemCount()
      );
      viewerSetContextXML = viewerSetContextXML.replace('<p1:patientInfo/>', patientNode);
      //To avoid the incorrect value getting appended
      viewerSetContextXML = viewerSetContextXML.replace(/Remove/g, '');
    }
    const lightsString = this.isLightThemeEnabled ? 'on' : 'off';
    viewerSetContextXML = viewerSetContextXML.replace('{{LIGHTS}}', lightsString);
    let docScanNode = '';
    if (this.contextService.getDocScanStatus(this.contextService.getCurrentContext()) === 'DOC_SCAN_INITIATED') {
      docScanNode = this.createDataNode('SHOW_DOC_SCAN_IN_PATFOLDER', 1, 'showdocscaninpatfolder');
    }
    viewerSetContextXML = viewerSetContextXML.replace('<p1:docScan/>', docScanNode);
    log.debug('viewerSetContextXML: ' + viewerSetContextXML);
    return viewerSetContextXML;
  }

  /**
   * This method is responsible for creating the PS launch setcontext request.
   *
   * state - state of PowerScribe
   * isUserNameRequired - Add user name in xml
   */
  getPSLaunchXml(state, isUserNameRequired, actionType, processName, createKey?: boolean) {
    log.info('Desktop Integration - xmlTemplateManagerService getPSLaunchXml()');
    let psLaunchXML = this.xmlTemplates.reportsetcontext;
    psLaunchXML = psLaunchXML.replace('{{SOURCENAME}}', this.reportOwnerInstance);
    psLaunchXML = psLaunchXML.replace('{{OWNERINSTANCE}}', this.reportOwnerInstance);
    psLaunchXML = psLaunchXML.replace('{{MODULENAME}}', this.reportModuleName);
    if (isUserNameRequired) {
      psLaunchXML = psLaunchXML.replace('{{USERNAME}}', this.REPORTING_CONFIG.USERNAME);
      const systemProps = this.utilService.getSystemProp();
      const isSSOFlagAbsentOrFalse = !systemProps['SSO_Flag'] || systemProps['SSO_Flag'] === 'false';

      if (this.diConfigService.getReportingConfig().SENDPASSWORD === 'true' && isSSOFlagAbsentOrFalse) {
        const password = this.diConfigService.getEncryptedPassword();
        const passwordNode = this.createDataNode('PASSWORD', 1, password?.encryptedPassword);
        psLaunchXML = psLaunchXML.replace('<p1:password/>', passwordNode);
      } else {
        if (
          this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] &&
          this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] === 'true' &&
          this.utilService.getSystemProp()['SSO_Flag'] &&
          this.utilService.getSystemProp()['SSO_Flag'] === 'true'
        ) {
          const password = this.diConfigService.getEncryptedPassword();
          const passwordNode = this.createDataNode('PASSWORD', 1, password?.encryptedPassword);
          psLaunchXML = psLaunchXML.replace('<p1:password/>', passwordNode);
        }
      }
    } else if (createKey) {
      const configKeyNode = this.createDataNode('REPORTING_SESSION_ID', 1, this.diConfigService.createConfigKey());
      psLaunchXML = psLaunchXML.replace('<p1:configkey/>', configKeyNode);
      psLaunchXML = psLaunchXML.replace('{{USERNAME}}', '');
    } else {
      psLaunchXML = psLaunchXML.replace('{{USERNAME}}', '');
    }
    psLaunchXML = psLaunchXML.replace('<p1:password/>', '');
    psLaunchXML = psLaunchXML.replace('<p1:configkey/>', '');
    psLaunchXML = psLaunchXML.replace('{{EVENT_SUBSCRIBERS}}', this.reportOwnerInstance);
    psLaunchXML = psLaunchXML.replace('{{STATE}}', state);
    psLaunchXML = psLaunchXML.replace('{{ACTIONTYPE}}', actionType);
    psLaunchXML = psLaunchXML.replace('{{CTXCONTROLLER}}', this.reportOwnerInstance);
    const logoutTimeoutNode = this.createDataNode('LOGOUT_TIMEOUT', 1, this.REPORTING_CONFIG.LOGOUT_TIMEOUT);
    psLaunchXML = psLaunchXML.replace('<p1:logouttimeout/>', logoutTimeoutNode);
    const processNameNode = this.createDataNode('PROCESS_NAME', 1, processName);
    psLaunchXML = psLaunchXML.replace('<p1:processName/>', processNameNode);
    // Remove the unused nodes from the template xml
    psLaunchXML = psLaunchXML.replace('<p1:accession/>', '');
    psLaunchXML = psLaunchXML.replace('<p1:closeoption/>', '');
    psLaunchXML = psLaunchXML.replace('<p1:sitename/>', '');
    return psLaunchXML;
  }

  getPSSetContextXml(accession, associateAcc, state, siteName, isDiscard, actionType) {
    log.info('Desktop Integration - xmlTemplateManagerService getPSSetContextXml()');
    let psSetContextXML = this.xmlTemplates.reportsetcontext;
    psSetContextXML = psSetContextXML.replace('{{SOURCENAME}}', this.reportOwnerInstance);
    psSetContextXML = psSetContextXML.replace('{{OWNERINSTANCE}}', this.reportOwnerInstance);
    psSetContextXML = psSetContextXML.replace('{{MODULENAME}}', this.reportModuleName);
    psSetContextXML = psSetContextXML.replace('{{USERNAME}}', this.REPORTING_CONFIG.USERNAME);
    psSetContextXML = psSetContextXML.replace('{{EVENT_SUBSCRIBERS}}', this.reportOwnerInstance);
    psSetContextXML = psSetContextXML.replace('{{STATE}}', state);
    psSetContextXML = psSetContextXML.replace('{{ACTIONTYPE}}', actionType);
    psSetContextXML = psSetContextXML.replace('<p1:logouttimeout/>', '');
    psSetContextXML = psSetContextXML.replace('<p1:processName/>', '');
    psSetContextXML = psSetContextXML.replace('<p1:password/>', '');
    psSetContextXML = psSetContextXML.replace('<p1:configkey/>', '');
    if (!_.isEmpty(accession)) {
      const accNumberNode = this.createDataNode('ACCESSION_NUMBER', 1, accession);
      psSetContextXML = psSetContextXML.replace('<p1:accession/>', accNumberNode);

      const associateAccNode = this.createDataNode('ASSOCIATE_ACCESSION_NUMBER', 1, associateAcc);
      psSetContextXML = psSetContextXML.replace('<p1:associateAccession/>', associateAccNode);

      // Set the close option corresponding to the accession number.
      if (isDiscard) {
        const closeOptionNode = this.createDataNode('CLOSEOPTIONS', 1, 'DISCARD');
        psSetContextXML = psSetContextXML.replace('<p1:closeoption/>', closeOptionNode);
      } else {
        psSetContextXML = psSetContextXML.replace('<p1:closeoption/>', '');
      }
    } else {
      psSetContextXML = psSetContextXML.replace('<p1:closeoption/>', '');
      psSetContextXML = psSetContextXML.replace('<p1:accession/>', '');
      psSetContextXML = psSetContextXML.replace('<p1:associateAccession/>', '');
    }
    const siteNameNode = this.createDataNode('SITENAME', 1, siteName);
    psSetContextXML = psSetContextXML.replace('<p1:sitename/>', siteNameNode);
    psSetContextXML = psSetContextXML.replace('{{CTXCONTROLLER}}', this.reportOwnerInstance);
    return psSetContextXML;
  }

  getAppLauncherSetContextXml(startCommandLine, stopCommandLine, siteName?) {
    log.info('Desktop Integration - xmlTemplateManagerService getAppLauncherSetContextXml()');
    let appLauncherSetContextXML = this.xmlTemplates.applaunchersetcontext;
    appLauncherSetContextXML = appLauncherSetContextXML.replace(
      '{{SOURCENAME}}',
      this.APPLAUNCHER_CONFIG.OWNERINSTANCE
    );
    appLauncherSetContextXML = appLauncherSetContextXML.replace(
      '{{CTXCONTROLLER}}',
      this.APPLAUNCHER_CONFIG.OWNERINSTANCE
    );
    appLauncherSetContextXML = appLauncherSetContextXML.replace('{{MODULENAME}}', this.APPLAUNCHER_CONFIG.MODULENAME);
    let destinationNode = '';
    if (!_.isUndefined(siteName)) {
      destinationNode = this.createDataNode('DESTINATION', 1, siteName);
    }
    appLauncherSetContextXML = appLauncherSetContextXML.replace('<p1:destination/>', destinationNode);
    log.debug('Desktop Integration - getAppLauncherSetContextXml Commandline : ' + startCommandLine);
    appLauncherSetContextXML = appLauncherSetContextXML.replace('{{COMMANDLINE}}', startCommandLine);
    let stopCommandLineNode = '';
    if (!_.isEmpty(stopCommandLine)) {
      stopCommandLineNode = this.createDataNode('STOP_COMMANDLINE', 1, stopCommandLine);
    }
    appLauncherSetContextXML = appLauncherSetContextXML.replace('<p1:stopcommandline/>', stopCommandLineNode);
    return appLauncherSetContextXML;
  }

  getPluginLoadXml(moduleName) {
    log.info('Desktop Integration - xmlTemplateManagerService getPluginLoadXml()');
    let loadXML = this.xmlTemplates.load;
    loadXML = loadXML.replace('{{SOURCENAME}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    loadXML = loadXML.replace('{{OWNERINSTANCE}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    loadXML = loadXML.replace('{{MODULENAME}}', moduleName);
    if (moduleName === this.WFM_CONTEXT_CONFIG.MODULENAME) {
      loadXML = loadXML.replace('{{OWNER_TYPE}}', 'NORMAL');
    } else {
      loadXML = loadXML.replace('{{OWNER_TYPE}}', this.UVIEWER_CONFIG.OWNER_TYPE);
    }
    let appNameNode = '';
    if (moduleName === this.VIEWERCONTEXT_CONFIG.MODULENAME || moduleName.indexOf('ReportingPlugin') !== -1) {
      appNameNode = this.createDataNode('APPNAME', 1, moduleName);
    }
    loadXML = loadXML.replace('<p1:appname/>', appNameNode);
    return loadXML;
  }

  /**
   * This method is responsible for creating the plugin close requests.
   *
   * moduleName - module name of plugin
   */
  getPluginCloseXml(moduleName) {
    log.info('Desktop Integration - xmlTemplateManagerService getPluginCloseXml()');
    let closeXML = this.xmlTemplates.close;
    closeXML = closeXML.replace('{{SOURCENAME}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    closeXML = closeXML.replace('{{OWNERINSTANCE}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    closeXML = closeXML.replace('{{MODULENAME}}', moduleName);
    let appNameNode = '';
    if (moduleName.indexOf('ReportingPlugin') !== -1) {
      appNameNode = this.createDataNode('APPNAME', 1, moduleName);
    }
    closeXML = closeXML.replace('<p1:appname/>', appNameNode);
    return closeXML;
  }

  /**
   * This method is responsible for creating the plugin subscribe requests.
   *
   * moduleName - module name of plugin
   * topicName - topic name of plugin
   */
  getSubscribeXml(topicName, moduleName) {
    log.info('Desktop Integration - xmlTemplateManagerService getSubscribeXml()');
    let subscribeXML = this.xmlTemplates.subscribe;
    subscribeXML = subscribeXML.replace('{{SOURCENAME}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    subscribeXML = subscribeXML.replace('{{TOPICNAME}}', topicName);
    subscribeXML = subscribeXML.replace('{{MODULENAME}}', moduleName);
    return subscribeXML;
  }

  getGExInboundContextXml(data, responseInfo, isFailure) {
    let wfmContextPayloadXML = this.xmlTemplates.wfmContextPayload;
    wfmContextPayloadXML = wfmContextPayloadXML.replace('{{SOURCENAME}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    wfmContextPayloadXML = wfmContextPayloadXML.replace('{{OWNERINSTANCE}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    wfmContextPayloadXML = wfmContextPayloadXML.replace('{{MODULENAME}}', this.WFM_CONTEXT_CONFIG.MODULENAME);
    wfmContextPayloadXML = wfmContextPayloadXML.replace('{{CONTEXT_RESPONSE_OWNER}}', data.contextResponseOwner);
    wfmContextPayloadXML = wfmContextPayloadXML.replace('{{RESULT}}', responseInfo.resultValue);
    wfmContextPayloadXML = wfmContextPayloadXML.replace('{{RESULT_MSG}}', responseInfo.resultMsg);
    wfmContextPayloadXML = wfmContextPayloadXML.replace('{{EXAM_IDENT}}', data.examIdent);
    if (isFailure) {
      const failureTypesNode = this.createDataNode(
        'FAILURE_TYPES',
        responseInfo.failureTypes.length,
        responseInfo.failureTypes.join(';')
      );
      wfmContextPayloadXML = wfmContextPayloadXML.replace('<p1:failureTypes/>', failureTypesNode);
    } else {
      wfmContextPayloadXML = wfmContextPayloadXML.replace('<p1:failureTypes/>', '');
    }

    let wfmContextXml = this.xmlTemplates.wfmContextSetContext;
    wfmContextXml = wfmContextXml.replace('{{SOURCENAME}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    wfmContextXml = wfmContextXml.replace('{{OWNERINSTANCE}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    wfmContextXml = wfmContextXml.replace('{{MODULENAME}}', this.WFM_CONTEXT_CONFIG.MODULENAME);
    wfmContextXml = wfmContextXml.replace('{{PAYLOAD}}', this.convertHTMLEntities(wfmContextPayloadXML));
    log.info('Desktop Integration - xmlTemplateManagerService getGExInboundContextXml() ' + wfmContextXml);
    return wfmContextXml;
  }

  getWfmContextRequestXml(topic: string, actionType: string, messageId: string, status?: string): string {
    let xml = xmlTemplates.wfmContextRequest;
    xml = xml.replace('{{TOPIC}}', topic);
    xml = xml.replace('{{SOURCENAME}}', this.WFM_CONTEXT_CONFIG.OWNERINSTANCE);
    xml = xml.replace('{{OWNERINSTANCE}}', this.WFM_CONTEXT_CONFIG.OWNERINSTANCE);
    xml = xml.replace('{{MESSAGE_ID}}', messageId);
    xml = xml.replace('{{ACTION_TYPE}}', actionType);
    if (isValidValue(status)) {
      const node = this.createDataNode(appConstants.wfmContextPlugin.STATUS, 1, status);
      xml = xml.replace('<p1:status/>', node);
    }
    return xml;
  }

  /**
   * This method is responsible for creating the viewer context XML.
   *
   * moduleName - module name of plugin
   * topicName - topic name of plugin
   */
  getViewerContextXml(data) {
    let viewerComparisonContextXML = this.xmlTemplates.comparisoncontext;
    viewerComparisonContextXML = viewerComparisonContextXML.replace(
      '{{SOURCENAME}}',
      this.UVIEWER_CONFIG.OWNERINSTANCE
    );
    viewerComparisonContextXML = viewerComparisonContextXML.replace(
      '{{OWNERINSTANCE}}',
      this.UVIEWER_CONFIG.OWNERINSTANCE
    );
    viewerComparisonContextXML = viewerComparisonContextXML.replace(
      '{{CTXCONTROLLER}}',
      this.UVIEWER_CONFIG.OWNERINSTANCE
    );
    viewerComparisonContextXML = viewerComparisonContextXML.replace('{{CONTEXT_IDENT}}', data.contextIdent);
    viewerComparisonContextXML = viewerComparisonContextXML.replace('{{CHANGE_TYPE}}', data.changeType);
    viewerComparisonContextXML = viewerComparisonContextXML.replace('{{CHANGE_DESCRIPTION}}', data.changeDescription);
    const listOfStudiesNode = this.createDataNode('LIST_OF_STUDIES', data.studiesLength, data.listOfStudies);
    viewerComparisonContextXML = viewerComparisonContextXML.replace('<p1:listofstudies/>', listOfStudiesNode);
    const primaryStudyNode = this.createDataNode('PRIMARY_STUDY', 1, data.primaryStudy);
    viewerComparisonContextXML = viewerComparisonContextXML.replace('<p1:primarystudy/>', primaryStudyNode);
    viewerComparisonContextXML = viewerComparisonContextXML.replace('<p1:listofremotestudies/>', '');
    log.info('Desktop Integration - getViewerContextXml : viewerSetContextXML' + viewerComparisonContextXML);
    return viewerComparisonContextXML;
  }

  getReportEventXml(data) {
    let reportEventXML = this.xmlTemplates.wfmContextReportEvent;
    reportEventXML = reportEventXML.replace('{{SOURCENAME}}', this.UVIEWER_CONFIG.OWNERINSTANCE);
    reportEventXML = reportEventXML.replace('{{EVENT_NAME}}', data.eventName);
    if (data.accession) {
      const accessions = data.accession.split(',');
      accessions.forEach((accession, index) => {
        accessions[index] = 'ACC:' + accession;
      });
      reportEventXML = reportEventXML.replace('{{EXAM_IDENT}}', accessions.join(','));
    }
    if (data.reportStatus) {
      reportEventXML = reportEventXML.replace('{{REPORT_STATUS}}', data.reportStatus);
    } else {
      reportEventXML = reportEventXML.replace('{{REPORT_STATUS}}', REPORT_STATUS.UNKNOWN);
    }
    reportEventXML = reportEventXML.replace('{{TAB_INTERRUPTED}}', data.tabInterruptStatus);
    reportEventXML = reportEventXML.replace('{{RESULT}}', data.result);
    return reportEventXML;
  }

  getTerminateXml(ownerInstance, applicationName) {
    log.info('Desktop Integration - xmlTemplateManagerService getTerminateXml()');
    let applicationTerminationXML = this.xmlTemplates.terminateapplicationcontext;
    applicationTerminationXML = applicationTerminationXML.replace('{{SOURCENAME}}', ownerInstance);
    applicationTerminationXML = applicationTerminationXML.replace('{{OWNERINSTANCE}}', ownerInstance);
    applicationTerminationXML = applicationTerminationXML.replace('{{APPLICATION_NAME}}', applicationName);
    return applicationTerminationXML;
  }

  createDataNode(nodeName, size, nodeValue) {
    log.info('Desktop Integration - xmlTemplateManagerService createDataNode()');
    let dataNode = this.xmlTemplates.generalnode;
    dataNode = dataNode.replace('{{NODE_NAME}}', nodeName);
    dataNode = dataNode.replace('{{SIZE}}', size);
    let stringNodes = '';
    if (size > 1) {
      const dataNodes = nodeValue.split(';');
      for (const node of dataNodes) {
        if (node !== '') {
          stringNodes = stringNodes + this.createStringNode(node) + '\n';
        }
      }
    } else {
      stringNodes = this.createStringNode(nodeValue);
    }
    dataNode = dataNode.replace('<p2:nodevalue/>', stringNodes);
    return dataNode;
  }

  createStringNode(nodeValue) {
    log.info('Desktop Integration - xmlTemplateManagerService createStringNode()');
    let stringNode = this.xmlTemplates.stringnode;
    stringNode = stringNode.replace('{{NODE_VALUE}}', nodeValue);
    return stringNode;
  }

  getUniqueID() {
    log.info('Desktop Integration - xmlTemplateManagerService getUniqueID()');
    const uniqueIdMax = 1000;
    const uniqueIdMin = 1;

    const date = new Date();
    // Random number generated between 1 and 1000 using cryptographically secure random number generator.
    const randomArray = new Uint32Array(1);
    crypto.getRandomValues(randomArray);
    const randomNumber = (randomArray[0] % (uniqueIdMax - uniqueIdMin + 1)) + uniqueIdMin;
    // Append time in second and millisecond to the random number.
    return randomNumber.toString() + date.getSeconds().toString() + date.getMilliseconds().toString();
  }

  getStudyClickTime() {
    log.info('Desktop Integration - xmlTemplateManagerService getStudyClickTime()');
    let studyClickTime = this.contextService.getStudyClickTime();
    // To handle getStudyClickTime call on load
    if (studyClickTime === 0) {
      studyClickTime = new Date().getTime();
    }
    return studyClickTime;
  }

  /**
   * This method is responsible for creating single patient node for the xml
   *
   * nodeName - node name
   * size - size of the node
   */
  createPatientNode(nodeName, nodeSize) {
    let patientNode = '';
    let i;
    if (nodeSize > 1) {
      //Multiple exams are opened in context
      //Split each Exam Identifiers and create data node for
      //each Exam Ident with Patient Info as value
      if (_.contains(nodeName, ';')) {
        const dataNodes = nodeName.split(';');
        for (i = 0; i < dataNodes.length; i++) {
          if (dataNodes[i] !== '' && !_.contains(dataNodes[i], ',')) {
            patientNode =
              patientNode + this.createDataNode(dataNodes[i], 1, this.setViewerCurrentItemContext(dataNodes[i]));
          } else {
            //Combination of mulitple exams and comparison exams are in context
            const comp = dataNodes[i].split(',');
            patientNode = patientNode + this.createDataNode(dataNodes[i], 1, this.setViewerCurrentItemContext(comp[0]));
          }
        }
      } else {
        //creating data nodes for comparison exams
        const dataCompNodes = nodeName.split(',');
        for (i = 0; i < dataCompNodes.length; i++) {
          if (dataCompNodes[i] !== '') {
            patientNode =
              patientNode +
              this.createDataNode(dataCompNodes[i], 1, this.setViewerCurrentItemContext(dataCompNodes[i]));
          }
        }
      }
    } else if (_.contains(nodeName, ',')) {
      //comparison exam identifier eg 123*NCD:334,NCD:567
      const dataComp = nodeName.split(',');
      patientNode = this.createDataNode(nodeName, 1, this.setViewerCurrentItemContext(dataComp[0]));
    } else {
      // single exam node eg: 1234*NCD:456
      patientNode = this.createDataNode(nodeName, 1, this.setViewerCurrentItemContext(nodeName));
    }
    return patientNode;
  }

  /**
   * This method is responsible for extracting value of operation node from the genericpluginloginoperation xml
   */
  getLoginOperationXml() {
    let returnVal = '';
    try {
      log.info('Desktop Integration - xmlTemplateManagerService getLoginOperationXml()');
      const parser = new DOMParser();
      const XMLStr = this.xmlTemplates.genericpluginloginoperation;
      if (XMLStr != '' && XMLStr != undefined) {
        const xmlDoc = parser.parseFromString(XMLStr, 'text/xml');
        const dataNodes = xmlDoc.getElementsByTagNameNS('http://ws.dotmed.gehc.com/', 'string');
        returnVal = dataNodes[0].childNodes.item(0).nodeValue;
        returnVal = returnVal.toString().replace(/\\/gi, '\\\\');
      }
    } catch (error) {
      log.error(
        'Error occured while parsing genericpluginloginoperation.xml. Kindly check xml and ensure login json is proper in xml.'
      );
    }
    return returnVal;
  }
}
